import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated, selectUserProfile } from "./authenticationSlice";
import { updateProfileNameAsync } from "./authenticationApi";
import { ROUTES } from "../../routes";

/**
 * Dismissed for this browser session only: skipping should not nag the instructor
 * again while they look around, but the name is still needed for member emails and
 * invoices, so the next sign-in asks once more. Settings can set it any time.
 */
const SKIPPED_KEY = "nameGateSkipped";

/** Routes that render without the app chrome; the gate stays out of their way too. */
const BARE_ROUTES: string[] = [ROUTES.LOGIN, ROUTES.REGISTER, ROUTES.LOGIN_VERIFY];

/**
 * One-field follow-up to the email-only trial start: an account created from an
 * address alone has no name, and member emails and invoices need one. Shown once,
 * on the first signed-in screen, until a name is saved — never on the portal or
 * the auth screens. Saving goes through the same profile-name endpoint Settings uses,
 * which also renames the linked instructor and refreshes the header.
 */
const NameGate = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const profile = useAppSelector(selectUserProfile);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [skipped, setSkipped] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SKIPPED_KEY) === "true";
    } catch {
      // Storage unavailable (private mode) — the gate simply shows.
      return false;
    }
  });

  const hasName = (profile?.name ?? "").trim() !== "";
  const isBareRoute =
    BARE_ROUTES.includes(location.pathname) || location.pathname.startsWith(ROUTES.PORTAL);
  if (!isAuthenticated || hasName || isBareRoute || skipped) {
    return null;
  }

  const skipForNow = () => {
    try {
      sessionStorage.setItem(SKIPPED_KEY, "true");
    } catch {
      // Nothing to persist — hiding it for this render is enough.
    }
    setSkipped(true);
  };

  const canSave = firstName.trim() !== "" && lastName.trim() !== "";

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!canSave || busy) {
      return;
    }
    setBusy(true);
    setFailed(false);
    const result = await dispatch(
      updateProfileNameAsync({ firstName: firstName.trim(), lastName: lastName.trim() })
    );
    setBusy(false);
    if (!updateProfileNameAsync.fulfilled.match(result)) {
      setFailed(true);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-10/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="name-gate-title"
    >
      <form
        onSubmit={handleSave}
        noValidate
        className="with-border w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-primary-10 lg:p-8"
      >
        <h1 id="name-gate-title" className="text-2xl font-bold text-grey-20 dark:text-white">
          {t("nameGate.title")}
        </h1>
        <p className="mt-3 leading-relaxed text-grey-30 dark:text-grey-70">{t("nameGate.body")}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm text-grey-20 dark:text-grey-80">
            {t("auth.firstName")}
            <input
              className="input"
              name="firstName"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-grey-20 dark:text-grey-80">
            {t("auth.lastName")}
            <input
              className="input"
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        {submitted && !canSave && (
          <p className="mt-3 text-sm text-red-700 dark:text-red-400">{t("nameGate.bothRequired")}</p>
        )}
        {failed && <p className="mt-3 text-sm text-red-700 dark:text-red-400">{t("nameGate.failed")}</p>}

        <button
          type="submit"
          disabled={busy}
          className="btn btn-primary mt-6 h-[46px] w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? t("nameGate.saving") : t("nameGate.save")}
        </button>

        <button
          type="button"
          onClick={skipForNow}
          disabled={busy}
          className="mt-3 w-full cursor-pointer border-none bg-transparent text-sm text-primary-40 hover:underline dark:text-primary-90"
        >
          {t("nameGate.later")}
        </button>
      </form>
    </div>
  );
};

export default NameGate;
